import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
  CreditCard as CreditCardIcon,
  Assignment as AssignmentIcon,
  Category as CategoryIcon,
  PieChart as PieChartIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { auth } from './firebase';
import useResponsive from './hooks/useResponsive';

// Context Provider
import { ExpenseProvider } from './contexts/ExpenseContext';
import Loader from './components/Loader';
import InstallPrompt from './components/InstallPrompt';
import MobileBottomNav from './components/MobileBottomNav';
import AccessibilityAnnouncer from './components/AccessibilityAnnouncer';

// Lazy load pages for better performance
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Expenses = React.lazy(() => import('./pages/Expenses'));
const AddExpense = React.lazy(() => import('./pages/AddExpense'));
const Reports = React.lazy(() => import('./pages/Reports'));
const Settings = React.lazy(() => import('./pages/Settings'));
const CreditCardConnections = React.lazy(() => import('./pages/CreditCardConnections'));
const DevLogin = React.lazy(() => import('./pages/DevLogin'));
const CategoryManager = React.lazy(() => import('./components/CategoryManager'));
const Categorization = React.lazy(() => import('./pages/Categorization'));
const EnvCheck = React.lazy(() => import('./pages/EnvCheck'));
const Analytics = React.lazy(() => import('./pages/Analytics'));

// Responsive drawer width - smaller on mobile for better UX
const drawerWidth = 260; // Desktop width

// Mobile-optimized drawer width (240px for 375px screens)
const mobileDrawerWidth = 240;

const DrawerHeader = styled(Toolbar)(({ theme }) => ({
  padding: theme.spacing(2.5, 2.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  minHeight: '88px !important',
  borderBottom: `1px solid rgba(255, 255, 255, 0.08)`,
  background: 'rgba(0, 0, 0, 0.02)',
  overflow: 'visible', // Allow logo hover effect to expand
}));

const LogoImage = styled('img')(({ theme }) => ({
  height: 52,
  width: 'auto',
  maxWidth: 60,
  objectFit: 'contain',
  flexShrink: 0,
  filter: 'brightness(1.1) saturate(1.3) drop-shadow(0 3px 8px rgba(0, 0, 0, 0.6))',
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.18) 100%)',
  borderRadius: '10px',
  padding: '8px',
  border: '1.5px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.08)',
    filter: 'brightness(1.2) saturate(1.5) drop-shadow(0 6px 16px rgba(0, 0, 0, 0.7))',
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.4) 100%)',
    border: '1.5px solid rgba(255, 255, 255, 0.5)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
  }
}));

// The BrandingSection styled component has been removed as part of the layout simplification.

function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const { isMobile, isDesktop } = useResponsive();
  const location = useLocation();
  const isAuthRoute = location.pathname.startsWith('/login');

  const navBackground = '#112C47';
  const navHighlight = '#8EE5D1';
  const navTextPrimary = 'rgba(255, 255, 255, 0.9)';
  const navTextSecondary = 'rgba(255, 255, 255, 0.65)';

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setUser(user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = useMemo(() => [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', hint: 'Pulse on your balance' },
    { text: 'Expenses', icon: <ReceiptIcon />, path: '/expenses', hint: 'Review transactions' },
    { text: 'Add Expense', icon: <AddIcon />, path: '/add-expense', hint: 'Capture a purchase' },
    { text: 'Credit Cards', icon: <CreditCardIcon />, path: '/credit-cards', hint: 'Connect accounts' },
    { text: 'Categorization', icon: <AssignmentIcon />, path: '/categorization', hint: 'Confirm suggestions' },
    { text: 'Categories', icon: <CategoryIcon />, path: '/categories', hint: 'Tailor spending map' },
    { text: 'Analytics', icon: <PieChartIcon />, path: '/analytics', hint: 'Insights & trends' },
    { text: 'Reports', icon: <AssessmentIcon />, path: '/reports', hint: 'Detailed reports' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings', hint: 'Personalize app' },
  ], []);

  const navigate = useNavigate();

  const handleLogout = async () => {
    // Clear demo mode if active
    localStorage.removeItem('demo_mode');
    localStorage.removeItem('demo_token');
    
    await auth.signOut();
    navigate('/login', { replace: true });
  };

  if (isAuthRoute) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #F7F5EF 0%, rgba(142,229,209,0.25) 100%)',
          p: { xs: 3, md: 6 },
        }}
      >
        <Routes>
          <Route path="/login" element={<DevLogin />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Box>
    );
  }

  const drawer = (
    <Box
      sx={{
        height: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        color: navTextPrimary,
        overflowY: 'auto', // Allow content to scroll
      }}
    >
      <DrawerHeader>
        <Box className="brand-header-wrapper" sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
          <Box className="logo-wrapper" sx={{ flexShrink: 0 }}>
            <LogoImage
              src="/Logos/BalanceBeam-Logo.svg"
              alt="BalanceBeam Financial Wellness Platform"
              onError={(e) => {
                e.target.src = "/balancebeam-logo-horizontal.png";
              }}
            />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  fontSize: '1.1rem',
                  lineHeight: 1.1,
                  whiteSpace: 'nowrap',
                }}
              >
                BalanceBeam
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#8EE5D1',
                  fontWeight: 500,
                  opacity: 0.9,
                  lineHeight: 1,
                  letterSpacing: '0.3px',
                  marginTop: '-2px', // Fine-tune vertical spacing
                }}
              >
                .money
              </Typography>
            </Box>
          </Box>
        </Box>
      </DrawerHeader>
      <List sx={{ px: 1.5, pt: 1, flexGrow: 1 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem disablePadding key={item.text} sx={{ mb: 0.5 }}>
              <ListItemButton
                component="a"
                href={item.path}
                selected={isActive}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: { xs: 1.5, md: 1.2 }, // Larger touch targets on mobile
                  minHeight: { xs: 48, md: 44 }, // Minimum 44px touch target
                  transition: 'all 0.2s ease',
                  color: navTextPrimary,
                  '& .MuiListItemText-primary': {
                    color: navTextPrimary,
                  },
                  '& .MuiListItemText-secondary': {
                    color: navTextSecondary,
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(142, 229, 209, 0.12)',
                    borderLeft: '3px solid rgba(142, 229, 209, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(142, 229, 209, 0.18)',
                    },
                    '& .MuiListItemIcon-root svg': {
                      color: '#8EE5D1',
                      filter: 'none',
                    },
                    '& .MuiListItemText-primary': {
                      color: 'rgba(255, 255, 255, 0.95)',
                      fontWeight: 600,
                    },
                    '& .MuiListItemText-secondary': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontWeight: 500,
                    },
                  },
                  '&:hover': {
                    transform: { xs: 'translateX(2px)', md: 'translateX(4px)' }, // Reduced transform on mobile
                    backgroundColor: 'rgba(142, 229, 209, 0.1)',
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  color: isActive ? navBackground : navTextSecondary,
                  minWidth: 36
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  secondary={item.hint}
                  primaryTypographyProps={{

fontWeight: isActive ? 600 : 500,
                    fontSize: '0.9rem',
                    sx: { color: navTextPrimary }
                  }}
                  secondaryTypographyProps={{
                    fontSize: 11,
                    sx: { color: navTextSecondary }
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ mt: 'auto' }}>
        <List sx={{ px: 1.5, pb: 2 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                px: 2,
                py: { xs: 1.5, md: 1.2 }, // Larger touch targets on mobile
                minHeight: { xs: 48, md: 44 }, // Minimum 44px touch target
                color: navTextPrimary,
                '&:hover': {
                  backgroundColor: 'rgba(142, 229, 209, 0.12)',
                  transform: { xs: 'translateX(1px)', md: 'translateX(2px)' }, // Reduced transform on mobile
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ListItemIcon sx={{ color: navTextSecondary, minWidth: 36 }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary="Sign out"
                secondary="We'll be right here when you return"
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: navTextPrimary }}
                secondaryTypographyProps={{ variant: 'caption', fontSize: 11, color: navTextSecondary }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>

      {/* Mobile-only AppBar */}
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          display: { xs: 'block', md: 'none' },
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: 56, px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LogoImage
                src="/Logos/BalanceBeam-Logo.svg"
                alt="BalanceBeam"
                onError={(e) => {
                  // Fallback to PNG if SVG fails
                  e.target.src = "/balancebeam-mark.png";
                }}
                sx={{
                  height: 28,
                  filter: 'none',
                  '&:hover': { transform: 'none' } // Disable hover effects for mobile header
                }}
              />
              <Typography variant="h6" sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.1rem',
                letterSpacing: '-0.01em'
              }}>
                BalanceBeam
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: mobileDrawerWidth,
              background: `linear-gradient(180deg, ${navBackground} 0%, #0c1f33 100%)`,
              borderRight: 'none',
            }
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: `linear-gradient(180deg, ${navBackground} 0%, #0c1f33 100%)`,
              borderRight: 'none', // Remove default border for a seamless look
            },
          }}
          open
        >
          {drawer}
        </Drawer>
</Box>

{/* Main Content */}

<Box
  component="main"
  sx={{
    flexGrow: 1,
    p: { xs: 2, sm: 2.5, md: 3 },
    pb: { xs: 9, md: 3 }, // Extra bottom padding on mobile for bottom nav
    width: isDesktop ? `calc(100% - ${drawerWidth}px)` : '100%',
    position: 'relative',
    mt: isMobile ? 7 : 0,
    minHeight: '100vh',
  }}
>
  <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 1.5, md: 2 }, px: { xs: 2, sm: 3, md: 3 } }}>

    <React.Suspense fallback={<Loader message="Loading page..." />}>
      <Routes>
        <Route path="/login" element={<DevLogin />} />
        <Route path="/env-check" element={<EnvCheck />} />
        <Route path="/" element={<PrivateRoute user={user} loading={loading}><Navigate to="/dashboard" replace /></PrivateRoute>} />
        <Route path="/dashboard" element={<PrivateRoute user={user} loading={loading}><Dashboard /></PrivateRoute>} />
        <Route path="/expenses" element={<PrivateRoute user={user} loading={loading}><Expenses /></PrivateRoute>} />
        <Route path="/add-expense" element={<PrivateRoute user={user} loading={loading}><AddExpense /></PrivateRoute>} />
        <Route path="/credit-cards" element={<PrivateRoute user={user} loading={loading}><CreditCardConnections /></PrivateRoute>} />
        <Route path="/categorization" element={<PrivateRoute user={user} loading={loading}><Categorization /></PrivateRoute>} />
        <Route path="/categories" element={<PrivateRoute user={user} loading={loading}><CategoryManager /></PrivateRoute>} />
        <Route path="/reports" element={<PrivateRoute user={user} loading={loading}><Reports /></PrivateRoute>} />
        <Route path="/analytics" element={<PrivateRoute user={user} loading={loading}><Analytics /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute user={user} loading={loading}><Settings /></PrivateRoute>} />
      </Routes>
    </React.Suspense>

  </Container>
</Box>

{/* Install Prompt for PWA */}
<InstallPrompt />

{/* Mobile Bottom Navigation */}
{!isAuthRoute && <MobileBottomNav />}

{/* Accessibility Announcer for Screen Readers */}
<AccessibilityAnnouncer />
</Box>
);
}

const PrivateRoute = ({ children, user, loading }) => {
  // Rotating promotional messages
  const loadingMessages = [
    "Taking control of your financial future...",
    "Your money, balanced and clear...",
    "Building your path to financial wellness...",
    "Making sense of your spending...",
    "Bringing clarity to your finances...",
    "Where smart money management begins...",
    "Transforming transactions into insights...",
    "Your financial story, beautifully organized...",
    "Empowering smarter spending decisions...",
    "Every shekel counts, tracked with precision...",
    "From chaos to clarity in seconds...",
    "Your money's best friend...",
    "Financial freedom starts here...",
    "See where your money goes, guide where it grows...",
    "Balance your budget, balance your life...",
    "Smart tracking for mindful spending...",
    "Your personal financial command center...",
    "Making money management effortless...",
    "Spend smart, save smarter...",
    "Know your numbers, own your future...",
  ];
  
  const randomMessage = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
  
  if (loading) {
    return <Loader message={randomMessage} />;
  }

  // Allow access if user is authenticated OR if in demo mode
  const isDemoMode = localStorage.getItem('demo_mode') === 'true';
  return (user || isDemoMode) ? children : <Navigate to="/login" />;
};

// App Component with Context Provider
export default function AppWrapper() {
  return (
    <ExpenseProvider>
      <App />
    </ExpenseProvider>
  );
}